import React from 'react';
import { formatTime, linkifyText } from '../utils/utils';
import { FiExternalLink } from 'react-icons/fi';
import { HiUserGroup } from 'react-icons/hi';
import { FaGlobe } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import { ROUTES } from '../App';

const ForwardedMessage = ({
    message,
    isFromCurrentUser,
    type = 'group', // 'group' or 'community'
    onViewClick,
    selectionMode,
    selected,
    onToggleReaction
}) => {
    const navigate = useNavigate();
    const original = message.originalGroup || message.originalCommunity;
    const resolvedType = message.originalCommunity
        ? 'community'
        : message.originalGroup
            ? 'group'
            : type;

    if (!original) return null;

    const handleViewClick = (e) => {
        e.stopPropagation();
        if (resolvedType === 'group') {
            navigate(`${ROUTES.GROUP_CHAT}/${original.id}${original.inviteToken ? `?invite=${original.inviteToken}` : ''}`);
        } else {
            navigate(`${ROUTES.COMMUNITY_CHAT}/${original.id}${original.inviteToken ? `?invite=${original.inviteToken}` : ''}`);
        }
        if (onViewClick) onViewClick();
    };

    const handleContentClick = (e) => {
        // If in selection mode, toggle selection when clicking the message
        if (selectionMode) {
            e.preventDefault();
            e.stopPropagation();
            if (onToggleReaction) {
                onToggleReaction(message.id, 'selected', selected ? message.currentReaction : null);
            }
        }
    };

    const getTypeText = () => {
        return resolvedType === 'group' ? 'Group' : 'Community';
    };

    return (
        <div className={`flex items-start gap-2 mt-1 ${isFromCurrentUser ? "justify-end" : "justify-start"} w-full`}>

            <div className={`md:max-w-[100%] max-w-[100%] ${isFromCurrentUser ? "items-end" : "items-start"}`}>
                {/* Forwarded message container - styled like WhatsApp */}
                <div
                    onClick={handleContentClick}
                    className={`
                        relative rounded-xl overflow-hidden transition-all w-fit border shadow-sm
                        ${isFromCurrentUser
                            ? 'bg-primary/95 text-white border-primary/70'
                            : 'bg-white/95 text-[#111b21] border-[#dbe6ee] dark:bg-[#202c33] dark:border-[#31404c] dark:text-[#e9edef]'
                        }
                        ${selectionMode && selected ? 'border-2 border-[#57a1be]' : ''}
                        ${selectionMode ? 'cursor-pointer' : ''}
                    `}
                >
                    {/* Top indicator - "Forwarded" label */}
                    <div className="flex items-center gap-1 px-3 pt-2">
                        <svg
                            className={`w-3.5 h-3.5 ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path d="M7 8a3 3 0 100-6 3 3 0 000 6zm.5 1.5A4.5 4.5 0 003 14v1h2v-1a2.5 2.5 0 015 0v1h2v-1a4.5 4.5 0 00-4.5-4.5zM14 8a2 2 0 100-4 2 2 0 000 4zm.5 1.5A3.5 3.5 0 0011 13v1h2v-1a1.5 1.5 0 013 0v1h2v-1a3.5 3.5 0 00-3.5-3.5z" />
                        </svg>
                        <span className={`text-xs font-medium tracking-wide ${isFromCurrentUser ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'}`}>
                            Shared
                        </span>
                    </div>

                    {/* Content */}
                    <div className="px-3 pb-2">
                        {/* Original message text */}
                        <p
                            className={`text-sm whitespace-pre-wrap break-words ${
                                isFromCurrentUser ? 'text-white' : 'text-[#111b21] dark:text-[#e9edef]'
                            }`}
                            style={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {linkifyText(
                                message.text,
                                isFromCurrentUser,
                                isFromCurrentUser
                                    ? 'text-white'
                                    : 'text-primary dark:text-[#57a1be]'
                            )}
                        </p>

                        {/* Group/Community Card - WhatsApp style */}
                        <div
                            onClick={handleViewClick}
                            className={`
                                mt-2 rounded-lg overflow-hidden cursor-pointer border
                                ${isFromCurrentUser
                                    ? 'bg-[#0d4960]/60 border-white/15'
                                    : 'bg-[#f2f7fb] dark:bg-[#1b2730] border-[#d7e3ec] dark:border-[#2e3d49]'
                                }
                            `}
                        >
                            <div className="flex items-center p-2 gap-3">
                                {/* Icon/Image */}
                                <div className="relative">
                                    <img
                                        src={original.photo || (
                                            resolvedType === 'group'
                                                ? "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"
                                                : "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/community-default.png?alt=media&token=default"
                                        )}
                                        alt={original.name}
                                        className="w-10 h-10 rounded-lg object-cover"
                                        onError={(e) => {
                                            e.target.src = "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346";
                                        }}
                                    />
                                    {/* Type indicator badge */}
                                    <div className={`
                                        absolute -bottom-1 -right-1 
                                        w-4 h-4 rounded-full 
                                        flex items-center justify-center
                                        ${resolvedType === 'group' ? 'bg-primary' : 'bg-green-500'}
                                        ring-1 ring-black
                                    `}>
                                        {resolvedType === 'group' ? (
                                            <HiUserGroup className="w-2.5 h-2.5 text-white" />
                                        ) : (
                                            <FaGlobe className="w-2.5 h-2.5 text-white" />
                                        )}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                        <h4 className={`font-medium text-sm truncate ${isFromCurrentUser ? 'text-white' : 'text-[#0f172a] dark:text-[#f1f5f9]'}`}>
                                            {original.name}
                                        </h4>
                                        <FiExternalLink className={`w-3 h-3 flex-shrink-0 ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-300'}`} />
                                    </div>
                                    <p className={`text-xs ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                                        {getTypeText()} • Tap to open
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center justify-end gap-1 mt-2">
                            <p className={`text-xs ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                                {formatTime(message.timestamp)}
                            </p>
                            {message.edited && (
                                <span className={`text-xs ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'} italic`}>
                                    (edited)
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForwardedMessage;